import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { AccentProvider } from "@/context/AccentContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { Toaster } from "sonner";
import AppShell from "@/components/AppShell";
import { Loading } from "@/components/common";

import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Team from "@/pages/Team";
import SecurityCenter from "@/pages/SecurityCenter";
import HomePage from "@/pages/Home";
import Today from "@/pages/Today";
import Jobs from "@/pages/Jobs";
import JobWorkflow from "@/pages/JobWorkflow";
import Estimator from "@/pages/Estimator";
import ShadowScan from "@/pages/ShadowScan";
import Equipment from "@/pages/Equipment";
import Academy from "@/pages/Academy";
import Performance from "@/pages/Performance";
import Alerts from "@/pages/Alerts";
import CommandCenter from "@/pages/CommandCenter";
import Crews from "@/pages/Crews";
import Customers from "@/pages/Customers";
import FinancialAnalytics from "@/pages/FinancialAnalytics";
import ProductionReadiness from "@/pages/ProductionReadiness";
import Settings from "@/pages/Settings";
import AdvancedSettings from "@/pages/AdvancedSettings";
import Feedback from "@/pages/Feedback";
import Organization from "@/pages/Organization";
import Timekeeping from "@/pages/Timekeeping";

function Protected({ children, roles }) {
  const { user } = useAuth();
  if (user === null) return <div className="min-h-screen flex items-center justify-center"><Loading label="Authenticating" /></div>;
  if (user === false) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return <AppShell>{children}</AppShell>;
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    // Route change → start at top (modals/drawers don't change pathname, so they're unaffected).
    window.scrollTo(0, 0);
    const main = document.querySelector("main");
    if (main) main.scrollTop = 0;
  }, [pathname]);
  return null;
}

function Shell() {
  const { user } = useAuth();
  return (
    <>
    <ScrollToTop />
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/" replace /> : <Login />} />
      <Route path="/forgot-password" element={user ? <Navigate to="/" replace /> : <ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/" element={<Protected><HomePage /></Protected>} />
      <Route path="/today" element={<Protected><Today /></Protected>} />
      <Route path="/jobs" element={<Protected><Jobs /></Protected>} />
      <Route path="/jobs/:id" element={<Protected><JobWorkflow /></Protected>} />
      <Route path="/estimator" element={<Protected><Estimator /></Protected>} />
      <Route path="/shadow-scan" element={<Protected><ShadowScan /></Protected>} />
      <Route path="/equipment" element={<Protected><Equipment /></Protected>} />
      <Route path="/academy" element={<Protected><Academy /></Protected>} />
      <Route path="/performance" element={<Protected><Performance /></Protected>} />
      <Route path="/alerts" element={<Protected><Alerts /></Protected>} />
      <Route path="/command-center" element={<Protected roles={["owner", "manager"]}><CommandCenter /></Protected>} />
      <Route path="/crews" element={<Protected roles={["owner", "manager"]}><Crews /></Protected>} />
      <Route path="/customers" element={<Protected roles={["owner", "manager"]}><Customers /></Protected>} />
      <Route path="/financial" element={<Protected roles={["owner"]}><FinancialAnalytics /></Protected>} />
      <Route path="/square-readiness" element={<Protected roles={["owner"]}><ProductionReadiness /></Protected>} />
      <Route path="/settings" element={<Protected><Settings /></Protected>} />
      <Route path="/settings/advanced" element={<Protected roles={["owner", "manager"]}><AdvancedSettings /></Protected>} />
      <Route path="/feedback" element={<Protected><Feedback /></Protected>} />
      <Route path="/timesheet" element={<Protected><Timekeeping /></Protected>} />
      <Route path="/organization" element={<Protected roles={["owner"]}><Organization /></Protected>} />
      <Route path="/team" element={<Protected roles={["owner"]}><Team /></Protected>} />
      <Route path="/security" element={<Protected><SecurityCenter /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <AccentProvider>
        <AuthProvider>
          <BrowserRouter>
            <Shell />
            <Toaster theme="dark" position="top-right" richColors />
          </BrowserRouter>
        </AuthProvider>
      </AccentProvider>
    </ThemeProvider>
  );
}
